import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismModal,
  PrismRadioGroup,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  DEFAULT_DISTRIBUTION_METHOD,
  DISTRIBUTION_METHODS,
  type DistributionMethod,
  isValidDistributionMethod,
} from 'lumen/utils/constants/budget-forecast-constants';
import { localCopy } from 'tracked-toolbox';

interface SettingsBudgetForecastSaveConfirmModalSignature {
  Args: {
    disableLastYearFinals: boolean;
    disableTwoYearFinals?: boolean;
    distributionMethod: DistributionMethod;
    isSaveTaskRunning: boolean;
    onCancel: () => void;
    onConfirm: (method: DistributionMethod) => void;
  };
}

export default class SettingsBudgetForecastSaveConfirmModal extends Component<SettingsBudgetForecastSaveConfirmModalSignature> {
  @service declare intl: Services['intl'];

  @localCopy('args.distributionMethod')
  declare _distributionMethod: DistributionMethod;

  get distributionMethod(): DistributionMethod {
    if (
      (this.args.disableLastYearFinals &&
        this._distributionMethod === DISTRIBUTION_METHODS.LAST_YEAR_FINALS) ||
      (this.args.disableTwoYearFinals &&
        this._distributionMethod === DISTRIBUTION_METHODS.LAST_TWO_YEAR_FINALS)
    ) {
      return DISTRIBUTION_METHODS.EQUAL;
    }
    return this._distributionMethod;
  }

  get selectedDistributionMethodLabel(): string {
    const distributionMethod =
      (this.args.disableLastYearFinals &&
        this.args.distributionMethod ===
          DISTRIBUTION_METHODS.LAST_YEAR_FINALS) ||
      (this.args.disableTwoYearFinals &&
        this.args.distributionMethod ===
          DISTRIBUTION_METHODS.LAST_TWO_YEAR_FINALS)
        ? DISTRIBUTION_METHODS.EQUAL
        : this.args.distributionMethod;

    if (distributionMethod === DISTRIBUTION_METHODS.EQUAL) {
      return this.intl
        .t('settings.budget_forecast.distribution_option.equally')
        .toLowerCase();
    }

    if (distributionMethod === DISTRIBUTION_METHODS.LAST_TWO_YEAR_FINALS) {
      return this.intl
        .t('settings.budget_forecast.distribution_option.2ly_finals')
        .toLowerCase();
    }

    return this.intl
      .t('settings.budget_forecast.distribution_option.ly_finals')
      .toLowerCase();
  }

  @action
  onConfirm(): void {
    this.args.onConfirm(this.distributionMethod);
  }

  @action
  updateDistributionMethod(event: Event): void {
    const method = (event.target as HTMLInputElement)?.value;

    if (isValidDistributionMethod(method)) {
      this._distributionMethod = method;
    } else {
      this._distributionMethod = DEFAULT_DISTRIBUTION_METHOD;
    }
  }

  <template>
    <PrismModal
      @isCloseDisabled={{@isSaveTaskRunning}}
      @onClose={{@onCancel}}
      @title={{t "settings.budget_forecast.existing_data_override"}}
      data-test-id="save-confirm-modal"
    >
      <:default>
        <div class="modal--padded flex flex-col gap-400">
          <p class="paragraph-300--regular">
            {{t "settings.budget_forecast.save_distribute_by"}}
            <span
              class="text-300--medium"
              data-test-id="selected-distribution-method-label"
            >
              {{this.selectedDistributionMethodLabel}}.
            </span>
          </p>
          <p class="paragraph-300--regular">
            {{t "settings.budget_forecast.choose_distribution_option"}}:
          </p>
          <PrismRadioGroup
            @disabled={{@isSaveTaskRunning}}
            @isHorizontal={{true}}
            @name="radio-group"
            @onChange={{this.updateDistributionMethod}}
            @value={{this.distributionMethod}}
            as |RadioGroup|
          >
            <RadioGroup.input
              @value={{DISTRIBUTION_METHODS.EQUAL}}
              data-test-id="distribution-method-option-equal"
            >
              {{t "settings.budget_forecast.distribution_option.equally"}}
            </RadioGroup.input>
            <span>
              {{#if @disableLastYearFinals}}
                <PrismTooltip
                  @text={{t "budget_forecast.distribute.ly_finals.disabled"}}
                />
              {{/if}}
              <RadioGroup.input
                @disabled={{@disableLastYearFinals}}
                @value={{DISTRIBUTION_METHODS.LAST_YEAR_FINALS}}
                data-test-id="distribution-method-option-last-year-finals"
              >
                {{t "settings.budget_forecast.distribution_option.ly_finals"}}
              </RadioGroup.input>
            </span>
            <span>
              {{#if @disableTwoYearFinals}}
                <PrismTooltip
                  @text={{t "budget_forecast.distribute.ly_finals.disabled"}}
                />
              {{/if}}
              <RadioGroup.input
                @disabled={{@disableTwoYearFinals}}
                @value={{DISTRIBUTION_METHODS.LAST_TWO_YEAR_FINALS}}
                data-test-id="distribution-method-option-last-two-year-finals"
              >
                {{t "settings.budget_forecast.distribution_option.2ly_finals"}}
              </RadioGroup.input>
            </span>
          </PrismRadioGroup>
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{@isSaveTaskRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onCancel}}
            data-test-id="save-confirm-modal-cancel"
          />
          <PrismButton
            @isDisabled={{@isSaveTaskRunning}}
            @isLoading={{@isSaveTaskRunning}}
            @label={{t "general.confirm_and_save"}}
            @onClick={{this.onConfirm}}
            data-test-id="save-confirm-modal-save"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
